import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';


// Since we'll use the CDN script for Angular 7 compatibility, 
// we declare LiveKit as a global variable.
declare var LiveKit: any;

@Injectable({
    providedIn: 'root'
})
export class ThorVoiceService {
    private room: any;
    private audioTrack: any;
    private dataReceivedSubject = new Subject<any>();
    public dataReceived$ = this.dataReceivedSubject.asObservable();

    constructor(private http: HttpClient) { }

    /**
     * Connect to the LiveKit room using the generated token.
     * @param token The LiveKit access token
     * @param roomUrl The LiveKit server URL (e.g. wss://...)
     */
    async connectToRoom(token: string, roomUrl: string): Promise<void> {
        try {
            const LK = (window as any).LivekitClient || (window as any).LiveKit;

            if (!LK) {
                throw new Error('LiveKit library not loaded. Please check your internet connection or index.html script tag.');
            }

            if (this.room && this.room.state === 'connected') {
                console.log('Thor Voice: Already connected to room', this.room.name);
                return;
            }

            if (!this.room) {
                this.room = new LK.Room();
            }

            this.room.on("dataReceived", (payload, participant) => {
                const decoded = new TextDecoder().decode(payload);
                const data = JSON.parse(decoded);
                this.dataReceivedSubject.next(data);
            });

            await this.room.connect(roomUrl, token);
            console.log('Thor Voice: Connected to room', this.room.name);
        } catch (error) {
            console.error('Thor Voice: Connection failed', error);
            throw error;
        }
    }

    /**
     * Enable microphone and publish track to the room.
     */
    async startMicrophone(): Promise<void> {
        try {
            const LK = (window as any).LivekitClient || (window as any).LiveKit;

            if (!this.room) {
                throw new Error('Room not connected');
            }

            // Create and publish audio track
            this.audioTrack = await LK.createLocalAudioTrack();
            await this.room.localParticipant.publishTrack(this.audioTrack);
            console.log('Thor Voice: Microphone streaming started');
        } catch (error) {
            console.error('Thor Voice: Failed to start microphone', error);
            throw error;
        }
    }

    /**
     * Stop streaming but stay in the room (for Thor to process audio).
     */
    async stopStreaming(): Promise<void> {
        try {
            if (this.room && this.audioTrack) {
                await this.room.localParticipant.unpublishTrack(this.audioTrack);
                this.audioTrack.stop();
                this.audioTrack = null;
                console.log('Thor Voice: Streaming stopped (track unpublished)');
            }
        } catch (error) {
            console.error('Thor Voice: Failed to stop streaming', error);
        }
    }

    /**
     * Fully disconnect and cleanup.
     */
    async disconnectRoom(): Promise<void> {
        try {
            await this.stopStreaming();
            if (this.room) {
                await this.room.disconnect();
                this.room = null;
                console.log('Thor Voice: Disconnected from room');
            }
        } catch (error) {
            console.error('Thor Voice: Disconnect failed', error);
        }
    }

    /**
     * Send text messages via LiveKit Data Channel
     */
    async sendData(message: string): Promise<void> {
        if (!this.room || this.room.state !== 'connected') {
            throw new Error('Cannot send data: Room not connected');
        }
        const encoded = new TextEncoder().encode(message);
        await this.room.localParticipant.publishData(encoded, { reliable: true });
        console.log('Thor Voice: Data published via LiveKit');
    }
}
